"use client";

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, UserPlus, Search, Pencil, ArrowRight, ArrowUpDown, ChevronUp, ChevronDown, Eye, Download, FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import CreateStaffModal from "./CreateStaffModal";
import EditStaffModal from "./EditStaffModal";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { API_ENDPOINTS } from "../../../constants/config";
import { toast } from "react-hot-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getFullName, getInitial, getStaffProfileUrl, findUserByUsername } from "../../../utils/urlUtils";
import { getStoredAuth } from "../../../utils/authStorage";
import { fetchMyBillingOverview } from "../../../services/billingService";
import Pagination from "../../Common/Pagination";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className="text-gray-800">{value || "Not provided"}</span>
  </div>
);

function StaffContent() {
  const navigate = useNavigate();
  const { staffName } = useParams();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedStaffProfile, setSelectedStaffProfile] = useState(null);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [designationFilter, setDesignationFilter] = useState("all");
  const [wingFilter, setWingFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [totalStaff, setTotalStaff] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0, alumni: 0, total: 0 });
  const [exportColumns, setExportColumns] = useState({
    name: true,
    staffId: true,
    email: true,
    phoneNumber: true,
    designation: true,
    department: true,
    dateOfJoining: false,
    maritalStatus: false,
    basicSalary: false,
    allowance: false,
    panNumber: false,
    epf: false,
    workShift: false,
    teacherType: false,
    contractType: false,
    address: false,
    bloodGroup: false,
    birthday: false,
  });
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [wings, setWings] = useState([]);
  const [loadingWings, setLoadingWings] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);

  const formatAddress = (address) => {
    if (!address) return "Not provided";
    const { street, city, state, pincode } = address;
    return [street, city, state, pincode].filter(Boolean).join(", ");
  };

  const getDesignationName = (designation) => {
    if (!designation) return "Not assigned";
    if (typeof designation === "object") return designation.name || "Not assigned";
    return designation;
  };
  const getDepartmentName = (department) => {
    if (!department) return "Not assigned";
    if (typeof department === "object") return department.name || "Not assigned";
    return department;
  };
  const getWingName = (wing) => {
    if (!wing) return "Not assigned";
    if (typeof wing === "object") return wing.name || "Not assigned";
    return wing;
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersStaff, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          status: statusFilter,
          search: debouncedSearchQuery || undefined,
          department: departmentFilter === "all" ? undefined : departmentFilter,
          designation: designationFilter === "all" ? undefined : designationFilter,
          wing: wingFilter === "all" ? undefined : wingFilter,
          sortBy: sortColumn,
          sortOrder: sortDirection,
          paginate: true,
          page: currentPage,
          limit: itemsPerPage,
        },
      });
      const staffList = response.data?.data?.staff ?? [];
      setStaff(staffList);
      setTotalStaff(Number(response.data?.data?.count || 0));
    } catch (err) {
      console.error("Failed to fetch staff:", err);
      setError(err.response?.data?.message || "Failed to fetch staff");
      setStaff([]);
      setTotalStaff(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersStaff, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          groupByStatus: true,
          search: debouncedSearchQuery || undefined,
          department: departmentFilter === "all" ? undefined : departmentFilter,
          designation: designationFilter === "all" ? undefined : designationFilter,
          wing: wingFilter === "all" ? undefined : wingFilter,
        },
      });

      const counts = response.data?.data?.statusCounts || { active: 0, inactive: 0, alumni: 0 };
      setStatusCounts({
        ...counts,
        total: (counts.active || 0) + (counts.inactive || 0) + (counts.alumni || 0),
      });
    } catch (err) {
      console.error("Failed to fetch staff status counts:", err);
      setStatusCounts({ active: 0, inactive: 0, alumni: 0, total: 0 });
    }
  };

  const fetchStaffProfileById = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.user(id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = response.data?.data?.user ?? response.data?.user;
      if (user && user.role === "staff") setSelectedStaffProfile(user);
    } catch (err) {
      console.error("Failed to fetch staff profile:", err);
      toast.error("Failed to load staff profile");
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.academicYears, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAcademicYears(response.data.data.academicYears || []);
    } catch (err) {
      console.error("Failed to fetch academic years:", err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, departmentFilter, designationFilter, wingFilter, statusFilter, sortColumn, sortDirection, itemsPerPage]);

  useEffect(() => {
    fetchStaff();
  }, [currentPage, itemsPerPage, statusFilter, debouncedSearchQuery, departmentFilter, designationFilter, wingFilter, sortColumn, sortDirection]);

  useEffect(() => {
    fetchStatusCounts();
  }, [debouncedSearchQuery, departmentFilter, designationFilter, wingFilter]);

  useEffect(() => {
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.departments, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDepartments(response.data?.data?.departments || []);
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, []);

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        setLoadingDesignations(true);
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.designations, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDesignations(response.data?.data?.designations || []);
      } catch (fetchDesignationError) {
        console.error("Failed to fetch designations:", fetchDesignationError);
      } finally {
        setLoadingDesignations(false);
      }
    };

    fetchDesignations();
  }, []);

  useEffect(() => {
    const fetchWings = async () => {
      try {
        setLoadingWings(true);
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.wings, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWings(response.data?.data?.wings || []);
      } catch (fetchWingError) {
        console.error("Failed to fetch wings:", fetchWingError);
      } finally {
        setLoadingWings(false);
      }
    };

    fetchWings();
  }, []);

  useEffect(() => {
    if (!staffName) return;
    const member = findUserByUsername(staff, staffName);
    if (member) {
      setSelectedStaffProfile(member);
      return;
    }
    if (staff.length === 0) return;
    let cancelled = false;
    const token = localStorage.getItem("token");
    axiosInstance
      .get(API_ENDPOINTS.usersStaff, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "all" },
      })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data?.staff ?? [];
        const found = findUserByUsername(list, staffName);
        if (found) setSelectedStaffProfile(found);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [staffName, staff]);

  const handleAddStaffClick = async () => {
    if (getStoredAuth().user?.role !== "schooladmin") {
      setIsCreateModalOpen(true);
      return;
    }
    try {
      const res = await fetchMyBillingOverview();
      const usage = res.data?.usage;
      if (res.data?.applicable && usage?.maxUsers != null && usage.currentUsers >= usage.maxUsers) {
        setLimitReached(true);
        return;
      }
    } catch {
      // Entitlement check failed — don't block staff creation on it, the
      // existing post-submit 403 handling still catches a real limit breach.
    }
    setLimitReached(false);
    setIsCreateModalOpen(true);
  };

  const handleViewProfile = (member) => {
    navigate(getStaffProfileUrl(member), { replace: true });
    setSelectedStaffProfile(member);
  };

  const getFilteredStaff = () => {
    return staff;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const fetchAllStaffForExport = async () => {
    const token = localStorage.getItem("token");
    const response = await axiosInstance.get(API_ENDPOINTS.usersStaff, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        status: statusFilter,
        search: debouncedSearchQuery || undefined,
        department: departmentFilter === "all" ? undefined : departmentFilter,
        designation: designationFilter === "all" ? undefined : designationFilter,
        wing: wingFilter === "all" ? undefined : wingFilter,
        sortBy: sortColumn,
        sortOrder: sortDirection,
      },
    });

    return response.data?.data?.staff ?? [];
  };

  const handleExportCSV = async () => {
    const staffMembers = await fetchAllStaffForExport();
    if (staffMembers.length === 0) return;

    const allPossibleColumns = {
      name: "Name",
      staffId: "Staff ID",
      email: "Email",
      phoneNumber: "Phone Number",
      designation: "Designation",
      department: "Department",
      dateOfJoining: "Date of Joining",
      maritalStatus: "Marital Status",
      basicSalary: "Basic Salary",
      allowance: "Allowance",
      panNumber: "PAN Number",
      epf: "EPF Number",
      workShift: "Work Shift",
      teacherType: "Employment Type",
      contractType: "Contract Type",
      address: "Address",
      bloodGroup: "Blood Group",
      birthday: "Birthday",
    };

    const headers = Object.keys(exportColumns)
      .filter((key) => exportColumns[key])
      .map(
        (key) =>
          allPossibleColumns[key] ||
          key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
      );

    let csvContent = headers.join(",") + "\n";

    staffMembers.forEach((member) => {
      const row = Object.keys(exportColumns)
        .filter((key) => exportColumns[key])
        .map((key) => {
          switch (key) {
            case "name":
              return getFullName(member);
            case "address":
              return formatAddress(member.address);
            case "birthday":
            case "dateOfJoining":
              return member[key]
                ? new Date(member[key]).toLocaleDateString()
                : "Not provided";
            case "department":
              return typeof member.department === "object" && member.department?.name
                ? member.department.name
                : member.department || "";
            case "workShift":
              return typeof member.workShift === "object" && member.workShift?.shiftName
                ? member.workShift.shiftName
                : (member.workShift || "");
            case "designation":
              return getDesignationName(member.designation);
            default:
              return member[key] || "";
          }
        })
        .map((value) => `"${value.toString().replace(/"/g, '""')}"`);

      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `staff_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportExcel = async (format = "xlsx") => {
    const staffMembers = await fetchAllStaffForExport();
    if (staffMembers.length === 0) return;

    const headers = Object.keys(exportColumns)
      .filter((key) => exportColumns[key])
      .map((key) => {
        return key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
      });

    const data = staffMembers.map((member) => {
      return Object.keys(exportColumns)
        .filter((key) => exportColumns[key])
        .map((key) => {
          switch (key) {
            case "name":
              return getFullName(member);
            case "address":
              return formatAddress(member.address);
            case "birthday":
            case "dateOfJoining":
              return member[key]
                ? new Date(member[key]).toLocaleDateString()
                : "Not provided";
            case "department":
              return typeof member.department === "object" && member.department?.name
                ? member.department.name
                : member.department || "";
            case "workShift":
              return typeof member.workShift === "object" && member.workShift?.shiftName
                ? member.workShift.shiftName
                : (member.workShift || "");
            case "designation":
              return getDesignationName(member.designation);
            default:
              return member[key] || "";
          }
        });
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Staff");
    const ext = format === "xls" ? "xls" : "xlsx";
    XLSX.writeFile(wb, `staff_${new Date().toISOString().split("T")[0]}.${ext}`);
  };

  const handleExportPDF = async () => {
    const staffMembers = await fetchAllStaffForExport();
    if (staffMembers.length === 0) return;

    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Staff Report", 14, 22);
      doc.setFontSize(11);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      const headers = Object.keys(exportColumns)
        .filter((key) => exportColumns[key])
        .map((key) => {
          return key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase());
        });

      const data = staffMembers.map((member) => {
        return Object.keys(exportColumns)
          .filter((key) => exportColumns[key])
          .map((key) => {
            switch (key) {
              case "name":
                return getFullName(member);
              case "address":
                return formatAddress(member.address);
              case "birthday":
              case "dateOfJoining":
                return member[key]
                  ? new Date(member[key]).toLocaleDateString()
                  : "Not provided";
              case "department":
                return typeof member.department === "object" && member.department?.name
                  ? member.department.name
                  : member.department || "";
              default:
                return member[key] || "";
            }
          });
      });

      autoTable(doc, {
        head: [headers],
        body: data,
        startY: 40,
      });

      doc.save(`staff_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handlePreviewExport = async () => {
    const staffMembers = await fetchAllStaffForExport();
    if (staffMembers.length === 0) return;
    
    setPreviewData(staffMembers.slice(0, 3)); // Show preview of first 3 records
    setIsPreviewOpen(true);
  };

  const handleUpdateStaff = async (staffId, updates) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.put(API_ENDPOINTS.user(staffId), updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchStaff();
      if (selectedStaffProfile?._id === staffId) {
        const updated = { ...selectedStaffProfile, ...updates };
        setSelectedStaffProfile(updated);
      }
      setIsEditModalOpen(false);
      setSelectedStaff(null);
      setSuccessMessage("Staff updated successfully");
      toast.success("Staff updated successfully");
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update staff";
      setError(msg);
      toast.error(msg);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(API_ENDPOINTS.user(staffId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchStaff();
      setSelectedStaffProfile(null);
      navigate("/dashboard/staff");
      setSuccessMessage("Staff deleted successfully");
      toast.success("Staff deleted successfully");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete staff");
    }
  };

  const renderStaffProfile = () => {
    if (!selectedStaffProfile) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 sm:p-6 rounded-t-lg">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="relative group">
                <img
                  src={
                    selectedStaffProfile.profileImage ||
                    "/placeholder.svg"
                  }
                  alt={getFullName(selectedStaffProfile)}
                  className="w-20 h-20 rounded-full border-4 border-white object-cover"
                />
                <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                  <button
                    type="button"
                    className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center hover:bg-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedStaffProfile.profileImage) {
                        window.open(selectedStaffProfile.profileImage, "_blank");
                      }
                    }}
                  >
                    <Eye className="w-3 h-3 text-gray-700" />
                  </button>
                </div>
              </div>
              <div className="text-white">
                <h2 className="text-2xl font-bold">
                  {getFullName(selectedStaffProfile)}
                  {selectedStaffProfile.username && (
                    <span className="ml-2 text-lg font-normal text-blue-100">
                      @{selectedStaffProfile.username}
                    </span>
                  )}
                </h2>
                <p className="text-blue-100">Staff</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <Button
                variant="secondary"
                className="bg-white/10 hover:bg-white/20 text-white"
                onClick={() => {
                  setSelectedStaff(selectedStaffProfile);
                  setIsEditModalOpen(true);
                }}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant="destructive"
                className="bg-red-500/10 hover:bg-red-500/20 text-white"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>

        <div className="border-b">
          <div className="flex flex-wrap px-4 sm:px-6">
            {["profile", "payroll", "documents"].map((tab) => (
              <Button
                key={tab}
                variant="ghost"
                className={`px-6 py-3 -mb-px ${
                  activeTab === tab
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() +
                  tab.slice(1).replace(/([A-Z])/g, " $1")}
              </Button>
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === "profile" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Role" value="Staff" />
                  <InfoRow label="Staff ID" value={selectedStaffProfile.staffId} />
                  <InfoRow label="Designation" value={getDesignationName(selectedStaffProfile.designation)} />
                  <InfoRow label="Department" value={getDepartmentName(selectedStaffProfile.department)} />
                  <InfoRow label="Wing" value={getWingName(selectedStaffProfile.wing)} />
                  <InfoRow
                    label="Date of Birth"
                    value={
                      selectedStaffProfile.birthday
                        ? new Date(selectedStaffProfile.birthday).toLocaleDateString()
                        : null
                    }
                  />
                  <InfoRow label="Blood Group" value={selectedStaffProfile.bloodGroup} />
                  <InfoRow label="Gender" value={selectedStaffProfile.gender} />
                  <InfoRow label="Marital Status" value={selectedStaffProfile.maritalStatus || "Not provided"} />
                  <InfoRow 
                    label="Date of Joining" 
                    value={
                      selectedStaffProfile.dateOfJoining
                        ? new Date(selectedStaffProfile.dateOfJoining).toLocaleDateString()
                        : null
                    } 
                  />
                  <InfoRow label="Employment Type" value={selectedStaffProfile.teacherType || "Not provided"} />
                  <InfoRow label="Contract Type" value={selectedStaffProfile.contractType || "Not provided"} />
                  <InfoRow label="Work Shift" value={typeof selectedStaffProfile.workShift === "object" && selectedStaffProfile.workShift?.shiftName ? selectedStaffProfile.workShift.shiftName : (selectedStaffProfile.workShift || "Not provided")} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Email" value={selectedStaffProfile.email} />
                  <InfoRow label="Phone Number" value={selectedStaffProfile.phoneNumber} />
                  <InfoRow
                    label="Address"
                    value={formatAddress(selectedStaffProfile?.address)}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Financial Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow 
                    label="Basic Salary" 
                    value={selectedStaffProfile.basicSalary ? `₹${selectedStaffProfile.basicSalary.toLocaleString()}` : null} 
                  />
                  <InfoRow 
                    label="Allowance" 
                    value={selectedStaffProfile.allowance ? `₹${selectedStaffProfile.allowance.toLocaleString()}` : null} 
                  />
                  <InfoRow label="PAN Number" value={selectedStaffProfile.panNumber} />
                  <InfoRow label="EPF Number" value={selectedStaffProfile.epf} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Bank Account Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow 
                    label="Account Holder Name" 
                    value={selectedStaffProfile.bankAccountDetails?.accountHolderName} 
                  />
                  <InfoRow 
                    label="Account Number" 
                    value={selectedStaffProfile.bankAccountDetails?.accountNumber} 
                  />
                  <InfoRow 
                    label="Bank Name" 
                    value={selectedStaffProfile.bankAccountDetails?.bankName} 
                  />
                  <InfoRow 
                    label="IFSC Code" 
                    value={selectedStaffProfile.bankAccountDetails?.ifsc} 
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "payroll" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Payroll Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Earnings</h4>
                      <div className="space-y-2">
                        <InfoRow label="Basic Salary" value={selectedStaffProfile.basicSalary ? `₹${selectedStaffProfile.basicSalary.toLocaleString()}` : "Not set"} />
                        <InfoRow label="Allowance" value={selectedStaffProfile.allowance ? `₹${selectedStaffProfile.allowance.toLocaleString()}` : "Not set"} />
                        <InfoRow label="Total Earnings" value={
                          selectedStaffProfile.basicSalary && selectedStaffProfile.allowance 
                            ? `₹${(selectedStaffProfile.basicSalary + selectedStaffProfile.allowance).toLocaleString()}`
                            : "Not available"
                        } />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Deductions</h4>
                      <div className="space-y-2">
                        <InfoRow label="EPF Number" value={selectedStaffProfile.epf || "Not set"} />
                        <InfoRow label="PAN Number" value={selectedStaffProfile.panNumber || "Not set"} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Documents & Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-500">
                    <p>Document management features coming soon...</p>
                    <p className="text-sm mt-2">This section will include:</p>
                    <ul className="text-sm mt-2 list-disc list-inside">
                      <li>Document uploads</li>
                      <li>Contract management</li>
                      <li>Certifications</li>
                      <li>Training records</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  };

  const paginatedStaff = getFilteredStaff();

  return (
    <div className="space-y-4">
      {selectedStaffProfile ? (
        <div>
          <Button
            onClick={() => {
              setSelectedStaffProfile(null);
              navigate("/dashboard/staff");
            }}
            className="mb-4 w-full bg-gray-500 text-white hover:bg-gray-600 sm:w-auto"
          >
            Back to Staff Management
          </Button>
          {renderStaffProfile()}
        </div>
      ) : (
        <>
          <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
            <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <CardTitle className="text-[#0263cc] text-xl font-semibold">
                    Staff Management
                  </CardTitle>
                  <CardDescription>
                    View and manage staff information
                  </CardDescription>
                </div>
                <div className="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center xl:w-auto xl:flex-wrap xl:justify-end">
                  <div className="relative w-full lg:min-w-[260px] lg:flex-1 xl:w-[320px] xl:flex-none">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search staff..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full rounded-lg border-[#0263cc]/20 bg-white py-2 pl-9 pr-4 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc]"
                    />
                  </div>
                  
                  {/* Department Filter */}
                  <div className="w-full lg:min-w-[200px] lg:flex-1 xl:w-auto xl:flex-none">
                    <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                      <SelectTrigger className="w-full rounded-lg border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] xl:w-[180px]">
                        <SelectValue placeholder="All Departments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept._id} value={dept._id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-full lg:min-w-[200px] lg:flex-1 xl:w-auto xl:flex-none">
                    <Select value={designationFilter} onValueChange={setDesignationFilter}>
                      <SelectTrigger className="w-full rounded-lg border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] xl:w-[180px]">
                        <SelectValue
                          placeholder={loadingDesignations ? "Loading designations..." : "All Designations"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Designations</SelectItem>
                        {designations.map((designation) => (
                          <SelectItem key={designation._id} value={designation._id}>
                            {designation.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-full lg:min-w-[200px] lg:flex-1 xl:w-auto xl:flex-none">
                    <Select value={wingFilter} onValueChange={setWingFilter}>
                      <SelectTrigger className="w-full rounded-lg border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] xl:w-[180px]">
                        <SelectValue
                          placeholder={loadingWings ? "Loading wings..." : "All Wings"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Wings</SelectItem>
                        {wings.map((wing) => (
                          <SelectItem key={wing._id} value={wing._id}>
                            {wing.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-full lg:min-w-[150px] lg:flex-1 xl:w-auto xl:flex-none">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full rounded-lg border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] xl:w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active ({statusCounts.active})</SelectItem>
                        <SelectItem value="inactive">Inactive ({statusCounts.inactive})</SelectItem>
                        <SelectItem value="alumni">Alumni ({statusCounts.alumni})</SelectItem>
                        <SelectItem value="all">All ({statusCounts.total})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-10 w-full shrink-0 rounded-lg border-[#0263cc]/20 p-0 text-red-600 hover:border-red-600 hover:text-red-600 sm:w-10 sm:rounded-full"
                        title="Export"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuItem onClick={handleExportCSV}>
                        <FileText className="w-4 h-4 mr-2" /> Export as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportExcel("xlsx")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xlsx
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportExcel("xls")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xls
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handlePreviewExport}>
                        <Eye className="w-4 h-4 mr-2" /> Preview Export
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    onClick={handleAddStaffClick}
                    className="h-10 w-full shrink-0 rounded-lg bg-green-500 p-0 hover:bg-green-600 sm:w-10 sm:rounded-full"
                    title="Add New Staff"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {error && (
                <Alert variant="destructive" className="m-4 rounded-lg bg-red-50 text-red-800 border-red-200">
                  <AlertDescription className="font-medium">{error}</AlertDescription>
                </Alert>
              )}
              {limitReached && getStoredAuth().user?.role === "schooladmin" && (
                <Alert className="m-4 rounded-lg bg-amber-50 text-amber-800 border-amber-200">
                  <AlertDescription className="font-medium">
                    Staff user limit reached. Visit Billing to view your plan or contact support to increase your
                    user limit.
                  </AlertDescription>
                </Alert>
              )}
              {successMessage && (
                <Alert className="m-4 rounded-lg bg-green-50 text-green-800 border-green-200">
                  <AlertDescription className="font-medium">{successMessage}</AlertDescription>
                </Alert>
              )}

              {loading ? (
                <div className="p-10 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm">Loading staff...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid gap-3 p-4 lg:hidden">
                    {paginatedStaff.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                        {totalStaff === 0
                          ? "No staff members yet. Click Add to create one."
                          : "No staff match your search."}
                      </div>
                    ) : (
                      paginatedStaff.map((member) => (
                        <button
                          key={member._id}
                          type="button"
                          className="rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition-colors hover:bg-gray-50"
                          onClick={() => handleViewProfile(member)}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100">
                                {member.profileImage ? (
                                  <img
                                    src={member.profileImage || "/placeholder.svg"}
                                    alt={getFullName(member)}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="font-semibold text-blue-600">
                                    {getInitial(member)}
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate font-semibold text-[#0263cc]">
                                  {getFullName(member)}
                                </p>
                                <p className="truncate text-sm text-gray-500">
                                  {member.staffId || "No staff ID"}
                                </p>
                              </div>
                            </div>
                            <span
                              className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
                                (member.status || "active") === "active"
                                  ? "bg-green-100 text-green-800"
                                  : (member.status || "") === "inactive"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {member.status || "active"}
                            </span>
                          </div>

                          <div className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                            <div>
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Designation</p>
                              <p className="text-gray-700">{getDesignationName(member.designation)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Department</p>
                              <p className="text-gray-700">{getDepartmentName(member.department)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Wing</p>
                              <p className="text-gray-700">{getWingName(member.wing)}</p>
                            </div>
                            <div className="sm:col-span-2">
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Email</p>
                              <p className="truncate text-gray-700">{member.email || "-"}</p>
                            </div>
                          </div>

                          <div className="mt-4 flex justify-end">
                            <span className="inline-flex items-center text-sm font-medium text-[#0263cc]">
                              View profile
                              <ArrowRight className="ml-1 h-4 w-4" />
                            </span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  <div className="hidden overflow-x-auto lg:block">
                    <table className="w-full min-w-[820px]">
                    <thead>
                      <tr className="bg-gray-50 border-y border-gray-200">
                        <th
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("name")}
                        >
                          <div className="flex items-center gap-1">
                            Staff Name
                            {sortColumn === "name" ? (
                              sortDirection === "asc" ? (
                                <>
                                  <ChevronUp className="h-4 w-4 text-[#0263cc] shrink-0" />
                                  <span className="text-[10px] font-normal text-gray-500 normal-case">(A→Z)</span>
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-4 w-4 text-[#0263cc] shrink-0" />
                                  <span className="text-[10px] font-normal text-gray-500 normal-case">(Z→A)</span>
                                </>
                              )
                            ) : (
                              <ArrowUpDown className="h-4 w-4 text-gray-400 shrink-0" />
                            )}
                          </div>
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("staffId")}
                        >
                          <div className="flex items-center gap-1">
                            Staff ID
                            {sortColumn === "staffId" ? (
                              sortDirection === "asc" ? (
                                <>
                                  <ChevronUp className="h-4 w-4 text-[#0263cc] shrink-0" />
                                  <span className="text-[10px] font-normal text-gray-500 normal-case">(Low→High)</span>
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-4 w-4 text-[#0263cc] shrink-0" />
                                  <span className="text-[10px] font-normal text-gray-500 normal-case">(High→Low)</span>
                                </>
                              )
                            ) : (
                              <ArrowUpDown className="h-4 w-4 text-gray-400 shrink-0" />
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedStaff.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                            {totalStaff === 0
                              ? "No staff members yet. Click Add to create one."
                              : "No staff match your search."}
                          </td>
                        </tr>
                      ) : (
                        paginatedStaff.map((member) => (
                          <tr
                            key={member._id}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => handleViewProfile(member)}
                          >
                            <td className="px-4 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                                  {member.profileImage ? (
                                    <img
                                      src={member.profileImage || "/placeholder.svg"}
                                      alt={getFullName(member)}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-blue-600 font-semibold">
                                      {getInitial(member)}
                                    </span>
                                  )}
                                </div>
                                <span className="font-medium text-[#0263cc] hover:text-[#0263cc]/80">
                                  {getFullName(member)}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {member.staffId || "—"}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {member.email || "—"}
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${
                                  (member.status || "active") === "active"
                                    ? "bg-green-100 text-green-800"
                                    : (member.status || "") === "inactive"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {member.status || "active"}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:text-[#0263cc]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewProfile(member);
                                }}
                              >
                                <ArrowRight className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    </table>
                  </div>
                </>
              )}

              <Pagination
                currentPage={currentPage}
                totalItems={totalStaff}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                currentPageCount={paginatedStaff.length}
                itemLabel="staff"
              />
            </CardContent>
          </Card>
        </>
      )}

      <CreateStaffModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        // Pass current academic year for staff ID generation
        currentAcademicYear={
          academicYears.find((year) => year.isCurrent) || null
        }
        onSuccess={() => {
          setIsCreateModalOpen(false);
          setLimitReached(false);
          fetchStaff();
        }}
        onLimitReached={() => setLimitReached(true)}
      />

      <EditStaffModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedStaff(null);
        }}
        staff={selectedStaff}
        onUpdate={handleUpdateStaff}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          if (selectedStaffProfile?._id) {
            handleDeleteStaff(selectedStaffProfile._id);
          }
          setIsDeleteDialogOpen(false);
        }}
      />

      {/* Export Preview Dialog */}
      {isPreviewOpen && previewData && (
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="w-[calc(100vw-1rem)] max-w-4xl max-h-[85vh] overflow-y-auto sm:w-full">
            <DialogHeader>
              <DialogTitle>Export Preview (First 3 Records)</DialogTitle>
            </DialogHeader>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    {Object.keys(exportColumns)
                      .filter((key) => exportColumns[key])
                      .map((key) => (
                        <th key={key} className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">
                          {key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((member, index) => (
                    <tr key={index}>
                      {Object.keys(exportColumns)
                        .filter((key) => exportColumns[key])
                        .map((key) => (
                          <td key={key} className="border border-gray-300 px-4 py-2 text-sm">
                            {key === "name" && getFullName(member)}
                            {key === "address" && formatAddress(member.address)}
                            {(key === "birthday" || key === "dateOfJoining") && 
                              (member[key] ? new Date(member[key]).toLocaleDateString() : "Not provided")}
                            {key === "department" && 
                              (typeof member.department === "object" && member.department?.name
                                ? member.department.name
                                : member.department || "")}
                            {key !== "name" && key !== "address" && key !== "birthday" && key !== "dateOfJoining" && key !== "department" && 
                              (key === "designation" ? getDesignationName(member.designation) : (member[key] || ""))}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
                Close
              </Button>
              <Button onClick={() => setIsPreviewOpen(false)}>
                Proceed with Export
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default StaffContent;
